/* eslint-disable import/no-deprecated
  -- https://office.visualstudio.com/OC/_workitems/edit/6091891
  -- TODO: Remove use of deprecated Environment
*/
import { getFluidDevOverrides, getLocalizationAttributes, enableUpToAudience, getRingForEnvironment } from '@fluidx/utilities';
import { determineLayoutMode, CancellationError } from '@fluidx/office-fluid-types';
import { ActivityTracker, NamespaceLoggerAdapter, sendTelemetryEvent } from '@fluidx/telemetry';
import { OfficeWebHostNamespace } from '../../Telemetry';
import { setupInitialIframeLayout } from '../../Iframe-shared/layout';
import { createBasicIframe } from '../../Iframe-shared/createBasicIframe';
import { attemptTokenPrefetch } from '../../Iframe-shared/attemptTokenPrefetch';
import * as devSettingsKeys from '../devOverrideKeys';
import { ComponentDivElementId } from '../../Iframe-shared/Constants';
import { generateSrcdocForIframeInside } from '../../Iframe-shared/srcdocGenerator';
import { getIframeIndexJsUrl } from './getIframeIndexJsUrl';
import { isFluidDebuggerEnabled } from '../../LoadHelpers/isFluidDebuggerEnabled';
import { getAllDomainsForContainer } from '@ms/bohemia-codeloader';
import { getPackageName } from '../../getPackageName';
import { prepareAdditionalDriverProcessing } from './AdditionalDriverProcessing';
import { mergeObjects } from '../../Utils';
import { npmVersionNumber } from '../../npm.package.version';
import { DependencyContainer } from '@fluidframework/synthesize';
import { overrideConsoleForIframe } from '../../Iframe-shared/overrideConsoleForIframe';
/**
 * The internal implementation of "loadComponentInSourcelessIframeGeneric".
 * This function does some pre-processing of the params from the original public-facing call.
 *
 * @param props The params of the internal props as well as initializeCallback
 * @param props.initializeConfig
 * @param props.iframeHostingElement
 * @param props.dependencySynthesizer
 * @param props.hostContext
 * @param props.config
 * @param props.options
 * @param props.additionalInfo
 * @param props.initializeCallback
 * @param props.loopCodeLoaderFactory
 */
export function loadComponentInSourcelessIframeGeneric({ initializeConfig, iframeHostingElement, dependencySynthesizer: originalDependencySynthesizer, hostContext: originalHostContext, config, options, additionalInfo, initializeCallback, loopCodeLoaderFactory }) {
    // Before beginning, replace the logger with a namespaced logger,
    // and pass that through as the modified HostConfig
    const { logger: hostLogger, ...partialConfig } = config;
    const namespacedLogger = hostLogger &&
        new NamespaceLoggerAdapter(OfficeWebHostNamespace, hostLogger, {
            inSourcelessIframe: true,
            // We want a persistent property for identifying sessions which are using the replay driver so we can potentially ignore those sessions in perf/reliability queries.
            isReplay: isFluidDebuggerEnabled() || undefined,
            owhVersion: npmVersionNumber
        });
    const newConfig = {
        ...partialConfig,
        logger: namespacedLogger
    };
    // For compatibility with the office-fluid-container, which still expects a HostContext.audience value,
    // we set it to the same value that was passed on the HostConfig.environment
    const hostContext = mergeObjects(originalHostContext, {
        audience: partialConfig.environment?.audience || 'Production'
    });
    const dependencySynthesizer = new DependencyContainer(originalDependencySynthesizer);
    dependencySynthesizer.register('HostContext', hostContext);
    return loadInSourcelessIframeHelper({
        initializeConfig,
        iframeHostingElement,
        dependencySynthesizer,
        hostContext,
        config: newConfig,
        options,
        additionalInfo,
        initializeCallback,
        loopCodeLoaderFactory
    });
}
// A helper function where the real "loadInSourcelessIframe" action takes place.
// By the time this code is invoked, the inputs are expected to have been sufficiently massaged
// by the publicly-exposed "loadComponentInSourcelessIframeInternal", and initialization can proceed in earnest.
function loadInSourcelessIframeHelper({ initializeConfig, iframeHostingElement, dependencySynthesizer, hostContext, config, options, additionalInfo, initializeCallback, loopCodeLoaderFactory }) {
    if (initializeConfig.type === 'Load') {
        const providers = dependencySynthesizer.synthesize({ TokenProvider: 'TokenProvider' }, undefined);
        Promise.resolve(providers.TokenProvider)
            .then((tokenProvider) => {
            if (tokenProvider) {
                return attemptTokenPrefetch(tokenProvider, initializeConfig.driverInfo);
            }
        })
            .catch(() => { });
    }
    const { logger } = config;
    // "loadComponent" activity tracker represents the full end-to-end loading of the component & container.
    // "bootIframe", meanwhile, just represents that an iframe was created and fired an "onload".
    // It does NOT represent the initialization call from within the iframe (that is captured
    // separately by an "iframeLoadAndInitialize" event that gets fired from within the iframe).
    const loadComponentActivityTracker = ActivityTracker.start("LoadComponentInIframe" /* Activity.LoadComponentInIframe */, logger, {
        addUserTiming: true
    });
    const bootIframeActivityTracker = ActivityTracker.start("IFrameBoot" /* Activity.IFrameBoot */, logger, {
        addUserTiming: true
    });
    const iframeLoadStartTime = Date.now();
    const additionalDriverProcessing = prepareAdditionalDriverProcessing(initializeConfig, config, options);
    let keepGoingWithLoad = true;
    let disposeAlreadyCalledOnce = false;
    let iframe;
    const onDispose = () => {
        if (disposeAlreadyCalledOnce) {
            return;
        }
        keepGoingWithLoad = false;
        disposeAlreadyCalledOnce = true;
        loadComponentActivityTracker.dispose();
        bootIframeActivityTracker.dispose();
        // Call the onDispose provided by host to do any custom cleanup needed
        // Host provided dispose could be responsible for pausing accidental logging for example on the CreateNew path or may have genuine log statements.
        // We don't want any logs to spill over the IframeDisposed signal, so make sure to call host provided dispose before IframeDispose event is logged
        const hostDispose = options.onDispose;
        hostDispose?.();
        if (logger) {
            sendTelemetryEvent(logger, {
                eventName: "IframeDisposed" /* OWHGenericEvent.IframeDisposed */,
                subCategory: "IframeDisposed" /* OWHEventSubCategories.IframeDisposed */
            });
            logger.dispose?.();
        }
        // Set the iframe reference to null, but don't dispose of the actual iframe DOM.
        // We leave it to the host to clear the DOM of the component div (which will probably get
        // batched with other DOM or React DOM updates anyway).
        // See more in the "Disposing iframes" section of "docs\ecosystem\hosting\OfficeWebHost.md".
        iframe = null;
    };
    let resumeLoad;
    let resumeLoadTime;
    const loadedInfo = new Promise(async (resolve, reject) => {
        const localization = getLocalizationAttributes();
        const layoutMode = options.layoutMode || determineLayoutMode(iframeHostingElement);
        // eslint-disable-next-line no-restricted-globals -- "document" here intentionally refers to "outside" document, which the iframe will be created on
        iframe = createBasicIframe(document, options.iframeTitle);
        setupInitialIframeLayout(layoutMode, iframe);
        const onIframeLoadOrResume = async () => {
            if (!keepGoingWithLoad) {
                return;
            }
            const initializeIframeActivityTracker = ActivityTracker.start("IFrameInitialize" /* Activity.IFrameInitialize */, logger, {
                addUserTiming: true
            });
            try {
                bootIframeActivityTracker.setResult(true);
                // By the time the onload fired, and since it's a sourceless iframe, contentWindow is not null
                const iframeWindow = iframe.contentWindow;
                overrideConsoleForIframe(iframeWindow);
                const argsForIframe = {
                    iframe: iframe,
                    initializeConfig,
                    dependencySynthesizer,
                    hostContext,
                    config,
                    options,
                    layoutMode,
                    iframeLoadStartTime,
                    localization,
                    bootstrapperStartTime: additionalInfo.bootstrapperStartTime,
                    resumeLoadTime,
                    onDispose,
                    cancellationErrorGenerator: (reason) => new CancellationError(reason),
                    proceedWithInternalLoad: additionalDriverProcessing.proceedWithDriverLoad,
                    loopCodeLoaderFactory
                };
                const loadResult = await initializeCallback(argsForIframe, loadComponentActivityTracker);
                runIfInTestingMode((options) => options.codeToInject, (codeToInject) => injectScriptCodeIntoWindow(iframeWindow.document, codeToInject));
                resolve(loadResult);
                initializeIframeActivityTracker.setResult(true);
                /* eslint-disable-next-line @typescript-eslint/no-explicit-any
        -- TODO: Using 'any' type defeats the purpose of TypeScript. Consider replacing it with a concrete type, or 'unknown'. */
            }
            catch (e) {
                initializeIframeActivityTracker.setResult(false, { errorCode: e.errorCode }, e);
                reject(e);
            }
        };
        if (options.partialLoadOnly) {
            const onIframeLoadPromise = new Promise((resolveIframeOnloadPromise) => {
                iframe.onload = () => {
                    resolveIframeOnloadPromise();
                    runIfInTestingMode((options) => options.afterIframeOnload, (afterIframeOnload) => afterIframeOnload());
                };
            });
            resumeLoad = () => {
                // If had already called "resume" once -- which we can tell by resumeLoadTime being set --
                // do nothing.
                if (resumeLoadTime) {
                    return;
                }
                resumeLoadTime = Date.now();
                // Disabling the lint rule for no floating promises below.
                // The onIframeLoadPromise will never reject, and the onIframeLoadOrResume has a try/catch that
                // properly handles rejecting the outer promise in case of error (and thus also will never reject).
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                onIframeLoadPromise.then(() => onIframeLoadOrResume());
            };
        }
        else {
            iframe.onload = onIframeLoadOrResume;
        }
        const preconnectUrls = [];
        // If we are loading a file, we can preconnect to some domains the driver tells us are essential for obtaining a snapshot
        const driverPreconnectDomains = initializeConfig.type === 'Load' ? initializeConfig.driverInfo.preconnectDomains : undefined;
        if (driverPreconnectDomains) {
            preconnectUrls.push(...driverPreconnectDomains);
        }
        // If we can derive the container code type from our inputs, we can also preconnect to any domains the code loader deems critical for boot performance
        let containerPackageName = undefined;
        if (initializeConfig.type === 'Load') {
            // Exception added when eslint rule was added, this should be revisited when modifying this code
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            containerPackageName = initializeConfig.driverInfo?.containerCodeHint;
        }
        else if (initializeConfig.type === 'CreateFromCode') {
            containerPackageName = getPackageName(initializeConfig.codeDetails);
        }
        if (containerPackageName) {
            const deploymentRing = getRingForEnvironment(config.environment);
            preconnectUrls.push(
            // Task 5294949: Convert from ContainerEnvironment to DeploymentRing
            // Both types currently hold the same value minus Local that only exists on ContainerEnvironment
            ...getAllDomainsForContainer(containerPackageName, deploymentRing));
        }
        const iframeBundleUrl = getFluidDevOverrides(devSettingsKeys.sourcelessIframeUrl) ||
            options.sourcelessIframeBundleUrl ||
            getIframeIndexJsUrl(getFluidDevOverrides(devSettingsKeys.sourcelessIframeFlavor) ||
                (options.useServiceDeliveredDriver ? 'serviceDeliveredDriver' : undefined));
        const providers = dependencySynthesizer.synthesize({ LoopTrustedTypesPolicy: 'LoopTrustedTypesPolicy' }, undefined);
        const trustedTypesPolicy = await providers.LoopTrustedTypesPolicy;
        const unsanitizedButSafelyGeneratedSrcdoc = generateSrcdocForIframeInside([...(additionalDriverProcessing.scriptUrlsToPrepend || []), iframeBundleUrl], preconnectUrls);
        // We don't really need to sanitize the srcdoc, because it is generated by us
        // and is guaranteed not to have anything unsafe about it.
        // On hosts that require trusted types, however, we still need to pass it through the
        // trusted types policy, or else the browser will block it.
        // For hosts that don't need it, can use the srcdoc directly, no extra sanitization needed.
        const sanitizedSrcdoc = trustedTypesPolicy?.createHTML(unsanitizedButSafelyGeneratedSrcdoc, { allowedTags: ['script'] }) ??
            unsanitizedButSafelyGeneratedSrcdoc;
        iframe.srcdoc = sanitizedSrcdoc;
        iframeHostingElement.appendChild(iframe);
    });
    const getIframe = () => iframe || undefined;
    const getRenderHostingElement = () => {
        const componentElement = (iframe?.contentDocument || iframe?.contentWindow?.document)?.getElementById(ComponentDivElementId);
        if (!componentElement) {
            throw new Error('Could not get iframe render hosting element');
        }
        return componentElement;
    };
    return {
        loadedInfo,
        resumeLoad,
        getIframe,
        getRenderHostingElement
    };
    /** Helper function that will run only if the option value is truthy AND if in a Test audience */
    function runIfInTestingMode(testingValueFetcher, callback) {
        if (enableUpToAudience('Automation', hostContext.audience)) {
            const forTestingOnly = options._DoNotUse_ForTestingOnly;
            if (forTestingOnly) {
                const value = testingValueFetcher(forTestingOnly);
                if (value) {
                    callback(value);
                }
            }
        }
    }
}
function injectScriptCodeIntoWindow(iframeDocument, content) {
    const scriptElem = iframeDocument.createElement('script');
    // Note: using "createTextNode", any relevant HTML entities will be properly escaped
    // (e.g., "console.log('</script>')"" would have broken everything when inserting code
    // via srcdoc, but would be OK when inserted via "createTextNode").
    scriptElem.appendChild(iframeDocument.createTextNode(content));
    iframeDocument.head.appendChild(scriptElem);
}
// cspell:ignore bootstrapper, prefetch, srcdoc, iframes, preconnect
//# sourceMappingURL=loadComponentInSourcelessIframeGeneric.js.map